import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller,  } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { TFunction } from 'i18next'

import { configOptions } from '../form-block/interfaces'
import { SRCheckbox } from '@sr/ui'
import { SRBlockRadioButton } from './components/radiobutton'
import { Button, BUTTON_TYPES, Form } from '@shared/ui'

import { useRouteState } from '@shared/utils'

import { ProfileCareActionsRouteState } from '../../actions/care-actions/state'
import { flattenObject } from '@shared/utils'

interface SectionBlockProps {
  id?: string
  data?: (t: TFunction) => configOptions
  field: string
}
interface FormData {
  [key: string]: any
}

export const SectionBlock: React.FC<SectionBlockProps> = ({
  id,
  data,
  field,
}) => {
  const { id: policyId } = useParams()
  const { t } = useTranslation()

  const { setClientPreferences, isLoading } = useRouteState(
    ProfileCareActionsRouteState,
    {
      policyId,
    },
  )

  const [isEditing, setIsEditing] = useState(false)
  const methodsChangePreferences = useForm<FormData>()
  const {
    control,
    formState: { errors },
  } = methodsChangePreferences

  const [originalRadioValue, setOriginalRadioValue] = useState<
    string | number | boolean | boolean[] | string[] | null | undefined
  >(undefined)
  const [originalRadioLabel, setOriginalRadioLabel] = useState<
    string | undefined
  >(undefined)
  const [originalCheckboxLabel, setOriginalCheckboxLabel] = useState<
    string[] | undefined
  >(undefined)

  const openEdit = () => {
    setIsEditing(true)
    setOriginalRadioValue(selectedRadioValue)
    setOriginalRadioLabel(selectedRadioLabel)
    setOriginalCheckboxLabel(selectedCheckboxLabel)

    if (
      configOptions?.type === 'radio' &&
      configOptions?.selected !== undefined
    ) {
      methodsChangePreferences.setValue(field, configOptions.selected as any)
      setSelectedRadioValue(configOptions.selected)
    }

    if (field === 'preventive_powers_service_interest') {
      const currentValue = configOptions?.selected
      let dbValues: any = {}

      if (currentValue === 2 && configOptions?.additionalOptions) {
        configOptions.additionalOptions.forEach((option) => {
          dbValues[String(option.value)] = option.selected ? 2 : 0
        })
      } else {
        dbValues = {
          legal_information_interest: 0,
          curatorship_detail_interest: 0,
          advance_directives_detail_interest: 0,
        }
      }

      methodsChangePreferences.setValue(`${field}_additional`, dbValues)
    }
  }

  const closeEdit = () => {
    setIsEditing(false)
  }

  const configOptions = data ? data(t) : null

  const [selectedRadioValue, setSelectedRadioValue] = useState<
    string | number | boolean | boolean[] | string[] | null | undefined
  >(undefined)
  const [selectedCheckboxLabel, setSelectedCheckboxLabel] = useState(
    configOptions?.selectedCheckboxLabels,
  )
  const [selectedRadioLabel, setSelectedRadioLabel] = useState(
    configOptions?.selectedRadioLabel,
  )

  useEffect(() => {
    if (isLoading || isEditing) {
      return
    }

    if (!configOptions) {
      return
    }

    if (field === 'preventive_powers_service_interest') {
      if (configOptions?.type === 'checkbox') {
        setSelectedRadioLabel(configOptions.selectedRadioLabel || '')
        setSelectedCheckboxLabel(configOptions.selectedCheckboxLabels)
        setSelectedRadioValue(2)
      } else if (configOptions?.type === 'radio') {
        setSelectedRadioValue(configOptions.selected)
        setSelectedRadioLabel(configOptions.selectedRadioLabel)
        setSelectedCheckboxLabel(undefined)
      }
    } else {
      if (configOptions?.type === 'checkbox') {
        setSelectedCheckboxLabel(configOptions.selectedCheckboxLabels)
      } else if (configOptions?.type === 'radio') {
        setSelectedRadioValue(configOptions.selected)
        setSelectedRadioLabel(configOptions.selectedRadioLabel)
      }
    }
  }, [isLoading, configOptions, isEditing, field])

  const extractFirstKey = (
    flattenedObject: Record<string, any>,
  ): string | null => {
    const keys = Object.keys(flattenedObject)
    return keys.length > 0 ? keys[0] : null
  }

  const validateAndSubmit = async () => {
    const data = methodsChangePreferences.getValues()

    if (field === 'preventive_powers_service_interest') {
      const preventiveValue = data[field] as any
      if (preventiveValue === 2) {
        const additionalData = data[`${field}_additional`]
        const hasSelectedCheckbox =
          additionalData &&
          Object.values(additionalData).some((value) => value === 2)

        if (!hasSelectedCheckbox) {
          methodsChangePreferences.setError(field, {
            type: 'manual',
            message: 'Debes seleccionar al menos un servicio',
          })
          return
        }
      }
    }

    await actualSubmit(data)
  }

  const actualSubmit = async (data: any) => {
    const firstkey = extractFirstKey(data)

    let finalData: any = data
    if (field === 'preventive_powers_service_interest') {
      const preventiveValue = data[field] as any
      if (preventiveValue === 2 && data[`${field}_additional`]) {
        const additionalData = data[`${field}_additional`]
        finalData = {
          ...data,
          ...additionalData,
        }
      } else if (preventiveValue !== 2) {
        finalData = {
          ...data,
          legal_information_interest: 0,
          curatorship_detail_interest: 0,
          advance_directives_detail_interest: 0,
        }
      }
      delete finalData[`${field}_additional`]
    }

    try {
      if (
        firstkey === 'additional_services' ||
        firstkey === 'prefered_channel' ||
        firstkey === 'schedule_prefer'
      ) {
        await setClientPreferences(flattenObject(finalData), id)
      } else {
        await setClientPreferences(finalData, id)
      }
    } catch (error) {
      console.error('Error al guardar preferencias:', error)
    }

    closeEdit()
  }

  function handleCancel() {
    setSelectedRadioValue(originalRadioValue)
    setSelectedRadioLabel(originalRadioLabel)
    setSelectedCheckboxLabel(originalCheckboxLabel)
    closeEdit()
  }

  return (
    <>
      <div
        className="mb-3 grid grid-cols-[3fr_1fr]"
        data-testid={field}
        onSubmit={(e) => {
          e.preventDefault()
          validateAndSubmit()
        }}
      >
        <h3 className="text-base font-semibold text-black mb-3">
          {configOptions?.title?.label || 'Estado del cliente'}
        </h3>
        <div className="flex flex-col items-end pr-[55px]">
          {isEditing ? (
            <button
              title={t('common:cancel')}
              className="underline text-primary text-base font-semibold cursor-pointer"
              onClick={handleCancel}
            >
              {t('common:cancel')}
            </button>
          ) : (
            <button
              onClick={openEdit}
              className="underline text-black text-base text-primary font-semibold cursor-pointer"
            >
              {t('userArea.profile.actions.editProfile.button')}
            </button>
          )}
        </div>

        {!isEditing && (
          <span
            className="text-dark-gray font-normal text-base"
            data-testid="service-info"
          >
            {/* Caso especial para poderes preventivos cuando está interesado */}
            {field === 'preventive_powers_service_interest' &&
              configOptions?.type === 'checkbox' && (
                <>
                  {selectedCheckboxLabel?.map((label, index) =>
                    label ? (
                      <span key={index} className="mb-4">
                        {label}
                        <br />
                      </span>
                    ) : (
                      ''
                    ),
                  )}
                </>
              )}

            {configOptions?.type === 'checkbox' &&
              field !== 'preventive_powers_service_interest' &&
              selectedCheckboxLabel?.map((label, index) =>
                label ? (
                  <span key={index}>
                    {label}
                    <br />
                  </span>
                ) : (
                  ''
                ),
              )}

            {selectedRadioLabel && 
             !(field === 'preventive_powers_service_interest' && configOptions?.type === 'checkbox') ? (
              <span>{selectedRadioLabel}</span>
            ) : selectedCheckboxLabel &&
              field !== 'preventive_powers_service_interest' ? (
              ''
            ) : !selectedCheckboxLabel ? (
              t('userArea.profile.actions.services.form.noInfo')
            ) : (
              ''
            )}
          </span>
        )}

        {isEditing && (
          <Form methods={methodsChangePreferences}>
            <div className="mb-3">
              {/* Radio buttons principales con checkboxes intercalados */}
              <ul className="text-base font-normal text-black">
                {configOptions?.options?.map((option, index) => (
                  <React.Fragment key={`${option.type}-${index}`}>
                    <li className="w-full mb-2">
                      {option.type === 'checkbox' ? (
                        <Controller
                          name={field}
                          control={control}
                          render={({ field: { onChange, value } }) => (
                            <SRCheckbox
                              label={option.label}
                              checked={option.selected}
                              onChange={(checked) => {
                                if (checked) {
                                  if (
                                    typeof option.value === 'string' ||
                                    typeof option.value === 'number'
                                  ) {
                                    const newValue = {
                                      ...value,
                                      [String(option.value)]: checked,
                                    }
                                    onChange(newValue)
                                    setSelectedCheckboxLabel((prevLabels) => {
                                      if (checked) {
                                        return [
                                          ...(prevLabels || []),
                                          option.label,
                                        ]
                                      } else {
                                        return (
                                          prevLabels?.filter(
                                            (label) => label !== option.label,
                                          ) || []
                                        )
                                      }
                                    })
                                  }
                                } else {
                                  const newValue = {
                                    ...value,
                                    [String(option.value)]: null,
                                  }

                                  onChange(newValue)
                                  setSelectedCheckboxLabel(undefined)
                                }
                              }}
                              className="accent-[#004039] !py-1"
                            />
                          )}
                        />
                      ) : (
                        <Controller
                          name={field}
                          control={control}
                          render={({ field: { onChange } }) => (
                            <SRBlockRadioButton
                              label={option.label}
                              className="accent-[#004039]"
                              value={
                                option.value !== undefined ? option.value : null
                              }
                              checked={selectedRadioValue === option.value}
                              onChange={(
                                val: string | number | boolean | null,
                                label,
                              ) => {
                                onChange(val as any)
                                setSelectedRadioValue(val)
                                setSelectedRadioLabel(label)
                                if (
                                  field ===
                                    'preventive_powers_service_interest' &&
                                  val !== 2
                                ) {
                                  const resetValues = {
                                    legal_information_interest: 0,
                                    curatorship_detail_interest: 0,
                                    advance_directives_detail_interest: 0,
                                  }
                                  methodsChangePreferences.setValue(
                                    `${field}_additional`,
                                    resetValues,
                                  )
                                }
                              }}
                              name={field}
                            />
                          )}
                        />
                      )}
                    </li>

                    {/* Checkboxes adicionales para poderes preventivos - aparecen después de "Tiene interés" */}
                    {field === 'preventive_powers_service_interest' &&
                      option.value === 2 &&
                      selectedRadioValue === 2 &&
                      configOptions?.additionalOptions && (
                        <li className="w-full mb-2">
                          <div className="ml-6 pl-4 border rounded-md border-gray-200">
                            <ul className="text-base font-normal text-black">
                              {configOptions.additionalOptions.map(
                                (additionalOption, additionalIndex) => (
                                  <li
                                    key={`additional-${additionalOption.type}-${additionalIndex}`}
                                    className="w-full mb-2"
                                  >
                                    <Controller
                                      name={`${field}_additional`}
                                      control={control}
                                      render={({
                                        field: { onChange, value },
                                      }) => (
                                        <SRCheckbox
                                          label={additionalOption.label}
                                          checked={
                                            Number(
                                              value?.[
                                                String(additionalOption.value)
                                              ],
                                            ) === 2
                                          }
                                          onChange={(checked) => {
                                            if (
                                              typeof additionalOption.value ===
                                                'string' ||
                                              typeof additionalOption.value ===
                                                'number'
                                            ) {
                                              const newValue = {
                                                ...value,
                                                [String(
                                                  additionalOption.value,
                                                )]: checked ? 2 : 0,
                                              }
                                              onChange(newValue)

                                              if (checked && errors[field]) {
                                                methodsChangePreferences.clearErrors(
                                                  field,
                                                )
                                              }
                                            }
                                          }}
                                          className="accent-[#004039] !py-1"
                                        />
                                      )}
                                    />
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                          {field === 'preventive_powers_service_interest' &&
                            errors[field] && (
                              <div className="text-red-500 text-xs mt-2 mb-3 ml-[40px]">
                                {String(errors[field]?.message || '')}
                              </div>
                            )}
                        </li>
                      )}
                  </React.Fragment>
                ))}
              </ul>
            </div>
            <Button type={BUTTON_TYPES.button} onClick={validateAndSubmit}>
              {t('common:save')}
            </Button>
          </Form>
        )}
      </div>
      <hr className="my-3" />
    </>
  )
}
